/**
 * Local fallbacks for keys that may be missing in Firebase language bundles.
 * Only newly added strings — Firebase values take precedence when present.
 */
const TRANSLATION_FALLBACKS = {
  en: {
    mark_booking_complete_web: "Mark trip as complete",
    mark_booking_complete_confirm:
      "Mark this booking as complete? The customer will no longer see it as an active trip.",
    mark_booking_complete_done: "Booking marked as complete.",
    cancel_payment_pending_web: "Cancel payment pending booking",
    cancel_payment_pending_confirm:
      "Cancel this booking with pending payment? The customer will no longer see it as an active trip.",
    cancel_payment_pending_done: "Booking cancelled.",
    cancelled_payment_pending_admin: "Cancelled by admin (payment pending)",
    upload_promo_image: "Upload Promo Image",
    force_update_section: "Force Update",
    force_update_enabled: "Enable mandatory update",
    min_ios_version: "Minimum iOS version",
    min_android_version: "Minimum Android version",
    min_ios_version_hint: "Must match App Store version (e.g. 1.9.10)",
    min_android_version_hint: "Must match versionCode (e.g. 191)",
    force_update_invalid_ios: "Invalid iOS version format",
    force_update_invalid_android: "Invalid Android versionCode",
    force_update_title: "Update required",
    force_update_body:
      "A new version is available. You must update to continue using the app.",
    force_update_button: "Update now",
    dispatch_tracking_title: "Driver notifications",
    dispatch_no_data:
      "No record for this booking (before dispatch log or without auto-dispatch).",
    dispatch_drivers_considered: "Drivers considered",
    dispatch_drivers_notified: "Drivers notified",
    dispatch_empty_list: "None",
    dispatch_col_driver: "Driver",
    dispatch_col_platform: "Platform",
    dispatch_col_status: "Status",
    dispatch_col_distance: "Distance",
    dispatch_notify_delivered: "Notified (OK)",
    dispatch_notify_failed: "Failed",
    dispatch_notify_sent: "Sent",
    stopover_point_n: "Stop {{n}}",
    stopover_final_destination: "Final destination",
    stopover_fee: "Stopover fee",
    stopovers_fare: "Stops ({{count}} × {{fee}})",
    stopover_fee_help:
      "Flat fee charged to the rider for each intermediate stop",
    stopover_wait_seconds: "Stopover wait time (seconds)",
    stopover_wait_seconds_help:
      "Time the driver will wait at each stop before being able to cancel the ride",
    stopover_wait_seconds_invalid:
      "Stopover wait time must be between 60 and 1800 seconds",
    stopover_arrival_title: "Arrived at stop",
    stopover_arrival_push:
      "The driver arrived at stop %{n} of %{m}. You have %{duration} to return.",
    stopover_timeout_title: "Stop wait time ended",
    stopover_time_ended_push:
      "The driver may cancel the trip if you do not return soon.",
    trip_resumed_title: "Trip in progress",
    trip_cancelled_timeout_title: "Trip cancelled",
    trip_cancelled_timeout_body:
      "The driver cancelled the trip because you exceeded the maximum stopover waiting time. Per service policy, no refund is issued.",
  },
  es: {
    mark_booking_complete_web: "Marcar viaje como completado",
    mark_booking_complete_confirm:
      "¿Marcar esta reserva como completada? El pasajero ya no la verá como viaje activo.",
    mark_booking_complete_done: "Reserva marcada como completada.",
    cancel_payment_pending_web: "Cancelar reserva con pago pendiente",
    cancel_payment_pending_confirm:
      "¿Cancelar esta reserva con pago pendiente? El pasajero ya no la verá como viaje activo.",
    cancel_payment_pending_done: "Reserva cancelada.",
    cancelled_payment_pending_admin: "Cancelado por admin (pago pendiente)",
    upload_promo_image: "Subir imagen de promo",
    force_update_section: "Actualización obligatoria",
    force_update_enabled: "Activar actualización obligatoria",
    min_ios_version: "Versión mínima iOS",
    min_android_version: "Versión mínima Android",
    min_ios_version_hint: "Debe coincidir con la versión en App Store (ej. 1.9.10)",
    min_android_version_hint: "Debe coincidir con versionCode (ej. 191)",
    force_update_invalid_ios: "Formato de versión iOS inválido",
    force_update_invalid_android: "versionCode de Android inválido",
    force_update_title: "Actualización requerida",
    force_update_body:
      "Hay una nueva versión disponible. Debes actualizar para continuar usando la app.",
    force_update_button: "Actualizar ahora",
    dispatch_tracking_title: "Notificaciones a conductores",
    dispatch_no_data:
      "Sin registro para esta reserva (anterior al log o sin auto-dispatch).",
    dispatch_drivers_considered: "Conductores considerados",
    dispatch_drivers_notified: "Conductores notificados",
    dispatch_empty_list: "Ninguno",
    dispatch_col_driver: "Conductor",
    dispatch_col_platform: "Plataforma",
    dispatch_col_status: "Estado",
    dispatch_col_distance: "Distancia",
    dispatch_notify_delivered: "Notificado (OK)",
    dispatch_notify_failed: "Falló",
    dispatch_notify_sent: "Enviado",
    stopover_point_n: "Parada {{n}}",
    stopover_final_destination: "Destino final",
    stopover_fee: "Comisión por parada",
    stopovers_fare: "Paradas ({{count}} × {{fee}})",
    stopover_fee_help:
      "Monto fijo cobrado al rider por cada parada intermedia",
    stopover_wait_seconds: "Tiempo de espera en parada (segundos)",
    stopover_wait_seconds_help:
      "Tiempo que el driver esperará en cada parada antes de poder cancelar el viaje",
    stopover_wait_seconds_invalid:
      "El tiempo de espera en parada debe estar entre 60 y 1800 segundos",
    stopover_arrival_title: "Llegada a la parada",
    stopover_arrival_push:
      "El driver llegó a la parada %{n} de %{m}. Tienes %{duration} para regresar.",
    stopover_timeout_title: "Tiempo de parada terminado",
    stopover_time_ended_push:
      "El driver podrá cancelar el viaje si no regresas pronto.",
    trip_resumed_title: "Viaje en marcha",
    trip_cancelled_timeout_title: "Viaje cancelado",
    trip_cancelled_timeout_body:
      "El driver canceló el viaje porque excediste el tiempo máximo de espera en la parada. Por política de servicio, no hay reembolso.",
  },
};

export function mergeTranslationFallback(langLocale, keyValuePairs = {}) {
  const fallback =
    TRANSLATION_FALLBACKS[langLocale] || TRANSLATION_FALLBACKS.en || {};
  return { ...fallback, ...keyValuePairs };
}
